import {
  OmniElement,
  OmniStyleElement,
  OmniTooltipElement,
  html,
  css,
  until,
} from 'omni-ui';

import { formatDate } from '../../helpers/utils.js';
import OmniAPI from '../../services/omni-api.js';

OmniStyleElement.register();
OmniTooltipElement.register();

export class ChannelWheelActivityCard extends OmniElement {
  /**
   * @property {String} image - URL of image (if defined)
   * @property {String} type - The type of activity (idea or asset)
   * @property {String} name - The name of the activity
   * @property {String} userId - Who last modified the activity
   * @property {String} date - When was the activity last modified
   * @property {String} text - A longer description of the activity
   * @property {String} documentURL - URL of an attached document (if defined)
   * @property {String} documentName - The name of the attached document (if defined)
   */
  static get properties() {
    return {
      image: { type: String },
      type: { type: String },
      name: { type: String },
      userId: { type: String },
      date: { type: String },
      text: { type: String },
      documentURL: { type: String },
      documentName: { type: String },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .channel-card {
          width: 23.214rem;
          height: 6.2rem;
          display: grid;
          position: relative;
          grid-template:
            'image . .' 0.3rem
            'image . channel' auto
            'image . type' 1fr
            'image . updated' auto
            'image . .' 0.3rem
            / 0.25fr 0.5rem 0.85fr;
          margin: 1px 0 1rem 1px;
          border-radius: 0.75rem;
          box-shadow:
            rgba(1, 33, 78, 0.15) 0 1px 10px -3px,
            rgba(1, 33, 78, 0.18) 0 15px 12px -9px;
        }
        .channel-card .default-img,
        .channel-card img {
          grid-area: image;
          width: 100%;
          object-fit: cover;
          object-position: center center;
          border-radius: 0.75rem 0 0 0.75rem;
          height: 100% !important;
        }
        .channel-card .default-img {
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: var(--color-gray-db);
          font-size: 2.5rem;
        }
        .channel-card .default-img omni-icon {
          --color-icon-lines: white;
        }
        .channel-card .card-type {
          grid-area: type;
          text-transform: capitalize;
          font-size: 0.85714rem;
          color: var(--color-shark);
        }
        .channel-card .card-channel {
          grid-area: channel;
          font-weight: bold;
          font-size: 0.92857rem;
        }
        .channel-card .card-updated {
          grid-area: updated;
          font-size: 0.7rem;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
        .channel-card .overlay {
          display: none;
        }
        .channel-card:hover .overlay {
          position: absolute;
          display: grid;
          grid-template:
            'text       text text   ' 1fr
            'attachment .    actions' auto
            / auto 1fr auto;
          height: 100%;
          width: 100%;
          border-radius: 0.75rem;
          background-color: rgba(20, 32, 51, 0.85);
          color: var(--color-core-light);
        }
        .channel-card .overlay .card-text {
          grid-area: text;
          font-size: 0.85714rem;
          margin: 0.6rem 0.8rem 0 0.8rem;
          overflow: hidden;
          mask-image: linear-gradient(180deg, var(--black) 50%, transparent);
        }
        .channel-card .overlay .card-attachment {
          grid-area: attachment;
          font-size: 0.85714rem;
          color: var(--color-primary);
          display: flex;
          align-items: center;
          margin-left: 0.8rem;
          min-width: 0;
        }
        .channel-card .overlay .card-attachment:hover {
          color: var(--color-core-light);
        }
        .channel-card .overlay .card-attachment-name {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .channel-card .overlay .card-actions {
          grid-area: actions;
          display: flex;
          margin: 0 0.5rem 0.5rem 0.5rem;
        }
        .overlay button {
          background-color: var(--color-transparent);
          border-color: var(--color-transparent);
          color: var(--color-primary);
          text-decoration: none;
        }
        .overlay omni-icon {
          fill: var(--color-core-light) !important;
        }
        .overlay button:hover {
          background-color: rgb(var(--rgb-core-light), 0.5) !important;
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.omniAPI = new OmniAPI(this.env);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * For names of the form:  "channel - activity - ad format"
   * where "ad format" == "activity", shorten the name to just "channel - activity"
   *
   * @returns {string} possibly shortened name
   */
  get #shortName() {
    const parts = this.name.split(' - ');
    if (parts[1] === parts[2]) return `${parts[0]} - ${parts[1]}`;
    return this.name;
  }

  #userName(userId) {
    return html`${until(this.omniAPI.getUser(userId, 'name'), '...')}`;
  }

  render() {
    const formattedDate = formatDate(this.date, true);

    return html`
      <omni-style>
        <div class="is-relative">
          <div class="channel-card">
            ${this.image
              ? html` <img src="${this.image}" alt="Channel Activity" /> `
              : html`
                  <div class="default-img">
                    <omni-icon icon-id="omni:app:contentAutomation"></omni-icon>
                  </div>
                `}
            <div class="card-channel">${this.#shortName}</div>
            <div class="card-type">${this.type}</div>
            <div class="card-updated">
              ${this.#userName(this.userId)}
              <span title="${formattedDate}">${formattedDate}</span>
            </div>
            <div class="overlay">
              <omni-tooltip class="card-text">
                <div slot="invoker">${this.text}</div>
                <div slot="content">${this.text}</div>
              </omni-tooltip>
              <div
                role="button"
                tabindex="0"
                class="card-attachment is-clickable ${this.documentURL
                  ? ''
                  : 'is-hidden'}"
                @click=${() => window.open(this.documentURL, '_blank')}
                @keydown=${e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    window.open(this.documentURL, '_blank');
                  }
                }}
              >
                <omni-icon
                  class="mr-2 is-size-4 is-flex-shrink-0"
                  icon-id="omni:informative:attachment"
                ></omni-icon>
                <omni-tooltip class="card-attachment-name">
                  <div slot="invoker">${this.documentName}</div>
                  <div slot="content">${this.documentName}</div>
                </omni-tooltip>
              </div>
              <div class="card-actions">
                ${this.actions.map(
                  action => html`
                    <omni-tooltip>
                      <button
                        slot="invoker"
                        class="button is-text is-size-7"
                        @click=${action.callback}
                      >
                        <omni-icon
                          class="is-size-4"
                          icon-id="${action.iconId}"
                        ></omni-icon>
                      </button>
                      <div slot="content">${action.tooltip}</div>
                    </omni-tooltip>
                  `,
                )}
              </div>
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-channel-activity-card',
  ChannelWheelActivityCard,
);
